import * as React from 'react';
import _ from 'lodash';

import type { ButtonProps } from '../../Button/Button';
import type { FilterGroupItem, FilterItem, FilterItemAll } from '../../../../types/Filter';
import Dropdown from '../../Dropdown/Dropdown';
import type { MoreListButtonOption, MoreListButtonProps } from '../../MoreListButton/MoreListButton';
import { isTruthy } from '../../../../modules/code-utils';
import MoreListButton from '../../MoreListButton/MoreListButton';
import useUpdateRef from '../../../../hooks/useUpdateRef';
import cn from '~modules/cn';
import FilterIcon from '../../../../assets/icons/v2design/basics/filter2.svg';
import AddIcon from '../../../../assets/icons/v2design/basics/add.svg';
import BreadcrumbsIcon from '../../../../assets/icons/v2design/basics/breadcrumbs.svg';
import './add-filter.scss';

type OnAddHandler = { (filter: FilterItemAll): void };

type BaseProps = {
    availableFilters: FilterItemAll[];
    onAdd?: OnAddHandler;
    className?: string;
};

type AddFilterButtonOwnProps = {
    buttonType?: ButtonProps['buttonType'];
    buttonSize?: ButtonProps['buttonSize'];
    icon?: ButtonProps['icon'];
    text?: string;
};

type AddFilterButtonProps = BaseProps & AddFilterButtonOwnProps;

export const AddFilterButton = (props: AddFilterButtonProps) => {
    const { className, buttonType = 'secondary', buttonSize = 'medium', icon = <FilterIcon />, text = 'Add filter' } = props;
    const { options, onChange } = useAddFilterOptions({ filters: props.availableFilters, onAdd: props.onAdd });
    return (
        <>
            <MoreListButton
                buttonProps={{
                    disabled: !options.length,
                    buttonType,
                    buttonSize,
                    icon,
                    className,
                    children: text
                }}
                itemClassName="add-filter-item"
                options={options}
                onChange={onChange}
            />
        </>
    );
};

const FIELD_FILTER_ID = 'SB_FIELD_FILTER';
const GROUP_FILTER_ID = 'SB_LOGICAL_FILTER';

type Option = MoreListButtonOption<string>;

const useAddFilterOptions = ({
    filters,
    onAdd
}: {
    filters: FilterItemAll[];
    onAdd?: OnAddHandler;
}): { options: Option[]; onChange?: MoreListButtonProps<string>['onChange'] } => {
    const onAddRef = useUpdateRef(onAdd);

    const { options, onChange } = React.useMemo(() => {
        const fieldFilters: FilterItem[] = [];
        const groupFilters: FilterGroupItem[] = [];

        filters.forEach((filterItem) => {
            if (filterItem.type === 'and' || filterItem.type === 'or') {
                groupFilters.push(filterItem);
            } else {
                fieldFilters.push(filterItem);
            }
        });

        if (!fieldFilters.length && !groupFilters.length) {
            // no field filters - disable control at all
            return { options: [] };
        }

        if (groupFilters.length > 0) {
            // there are logical groups available
            const options = [
                fieldFilters.length > 0
                    ? {
                          id: FIELD_FILTER_ID,
                          label: 'Add filter rule',
                          leftView: <AddIcon className="add-filter-icon" />
                      }
                    : undefined,
                ...(groupFilters.map((groupFilter) => ({
                    id: `${GROUP_FILTER_ID}_${groupFilter.type}`,
                    label: groupFilters.length > 1 ? `Add filter ${groupFilter.type.toUpperCase()} group` : 'Add filter group',
                    leftView: <BreadcrumbsIcon className="add-filter-icon" />
                })) ?? [])
            ].filter(isTruthy);
            return {
                options,
                onChange: (option: Option) => {
                    let filterItem;
                    if (option.id === FIELD_FILTER_ID && fieldFilters[0]) {
                        filterItem = fieldFilters[0];
                    } else if (option.id.startsWith(GROUP_FILTER_ID)) {
                        const filterType = option.id.replace(`${GROUP_FILTER_ID}_`, '');
                        filterItem = groupFilters.find((filter) => filter.type === filterType);
                    }
                    if (!filterItem) {
                        throw new Error('Filter item is not found');
                    }
                    onAddRef.current?.(filterItem);
                }
            };
        }

        // only fields
        const options = fieldFilters.map<Option>((filterItem) => ({
            id: filterItem.field,
            label: filterItem.name,
            leftView: filterItem.icon
        }));

        return {
            options,
            onChange: (option: Option) => {
                const filter = fieldFilters.find((filterItem) => filterItem.field === option.id);
                if (!filter) {
                    throw new Error('Filter not found');
                }
                onAddRef.current?.(filter);
            }
        };
    }, [filters, onAddRef]);

    return { options, onChange };
};

type AddFilterDropdownProps = Omit<BaseProps, 'availableFilters'> & {
    availableFilters: FilterItem[];
    value: FilterItem;
};

export const AddFilterDropdown = (props: AddFilterDropdownProps) => {
    const options = props.availableFilters.map((filterItem) => ({
        name: filterItem.name,
        value: filterItem.field,
        tip: filterItem.name,
        tipOverflow: true,
        leftView: filterItem.icon
    }));
    const { value } = props;
    return (
        <Dropdown
            className={cn('add-filter-dropdown', props.className)}
            menuClassName="add-filter-dropdown-tooltip"
            options={options}
            value={value.field}
            onChange={(fieldName) => {
                const filterItem = props.availableFilters.find((filterItem) => filterItem.field === fieldName)!;
                props.onAdd?.(filterItem);
            }}
            preTextIcon={value.icon}
            dataTip={value.name}
            dataTipOverflow
            sameWidth
        />
    );
};
